/**
 * Realtime → in-app notification bridge (TO-468). Maps pushable event types to a
 * status-bar notification. The payload is a HINT: read it defensively and fall
 * back to generic copy rather than ever render `undefined`. The offer render
 * failures (TO-466) use fixed copy and read no payload at all.
 */
import { EventType, type RealtimeMessage, type RealtimePushEventType } from '@today/types'
import { NotificationLevel } from '@today/ui-components'
import type { NotificationInput } from '@/notifications/notification-store'

export const PING_TITLE = 'Realtime test message received'
export const GENERIC_PING_DESCRIPTION = 'Diagnostic ping delivered over the realtime channel.'
export const PDF_FAILED_TITLE = 'PDF generation failed'
export const PDF_FAILED_DESCRIPTION = 'The PDF for an offer could not be generated.'
export const LANDING_FAILED_TITLE = 'Landing page generation failed'
export const LANDING_FAILED_DESCRIPTION = 'The landing page for an offer could not be generated.'
const MAX_MESSAGE_LENGTH = 200

/** `data[key]` when it is a string of 1–`max` chars, else `undefined`. */
export function stringField(data: Record<string, unknown> | undefined, key: string, max: number): string | undefined {
	const value = data?.[key]
	return typeof value === 'string' && value.length >= 1 && value.length <= max ? value : undefined
}

const NOTIFICATION_MAP: Partial<Record<RealtimePushEventType, (message: RealtimeMessage) => NotificationInput>> = {
	[EventType.REALTIME_DIAGNOSTIC_PING]: (message) => ({
		level: NotificationLevel.INFO,
		title: PING_TITLE,
		description: stringField(message.data, 'message', MAX_MESSAGE_LENGTH) ?? GENERIC_PING_DESCRIPTION,
	}),
	[EventType.OFFER_LANDING_FAILED]: () => ({
		level: NotificationLevel.ERROR,
		title: LANDING_FAILED_TITLE,
		description: LANDING_FAILED_DESCRIPTION,
	}),
	[EventType.OFFER_PDF_FAILED]: () => ({
		level: NotificationLevel.ERROR,
		title: PDF_FAILED_TITLE,
		description: PDF_FAILED_DESCRIPTION,
	}),
}

/** A dispatcher handler that pushes the mapped notification, if any. */
export function createNotificationBridge(
	push: (input: NotificationInput) => unknown,
): (message: RealtimeMessage) => void {
	return (message) => {
		const toNotification = NOTIFICATION_MAP[message.type as RealtimePushEventType]
		if (toNotification) push(toNotification(message))
	}
}
